import {
  Button,
  Card,
  Dropdown,
  Empty,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  message,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useIsCompact } from "../../hooks/useIsCompact";
import {
  DeleteOutlined,
  WarningOutlined,
  StopOutlined,
  MoreOutlined,
  EyeOutlined,
} from "@ant-design/icons";

import {
  IReport,
  IUser,
  ReportCategory,
  ReportStatus,
  ReportType,
} from "../../model/Interfaces";
import { Api } from "@model";

const ReportListPage = () => {
  const isCompact = useIsCompact();
  const [reports, setReports] = useState<IReport[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ReportStatus[]>([
    ReportStatus.PENDING,
    ReportStatus.UNDER_REVIEW,
  ]);
  const [selectedReport, setSelectedReport] = useState<IReport | null>(null);
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const [banModalVisible, setBanModalVisible] = useState(false);
  const [descriptionModalVisible, setDescriptionModalVisible] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState("");
  const [warningMessage, setWarningMessage] = useState("");
  const [banMessage, setBanMessage] = useState("");
  const [banPeriodDays, setBanPeriodDays] = useState(7);

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, statusFilter]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = statusFilter.length > 0 ? statusFilter.join(",") : "";
      const response = await Api.get(
        `/api/admin/panel/reports?page=${page}&limit=${limit}${
          statusParam ? `&status=${statusParam}` : ""
        }`
      );
      setReports(response.data.data.reports);
      setTotal(response.data.data.total);
    } catch (error) {
      message.error("Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter]);

  const handleDelete = async (reportId: string) => {
    try {
      await Api.delete(`/api/admin/panel/reports/${reportId}`);
      message.success("Report deleted successfully");
      fetchReports();
    } catch (error) {
      message.error("Failed to delete report");
    }
  };

  const handleSendWarning = async () => {
    if (!selectedReport || !warningMessage.trim()) {
      message.error("Please enter a warning message");
      return;
    }

    try {
      await Api.put(`/api/admin/panel/reports/${selectedReport._id}/warn`, {
        message: warningMessage,
      });
      message.success("Warning sent successfully");
      setWarningModalVisible(false);
      setWarningMessage("");
      setSelectedReport(null);
      fetchReports();
    } catch (error) {
      message.error("Failed to send warning");
    }
  };

  const handleBanUser = async () => {
    if (!selectedReport || !banMessage.trim()) {
      message.error("Please enter a ban message");
      return;
    }

    try {
      await Api.put(`/api/admin/panel/reports/${selectedReport._id}/ban`, {
        banPeriodDays,
        message: banMessage,
      });
      message.success("User banned successfully");
      setBanModalVisible(false);
      setBanMessage("");
      setBanPeriodDays(7);
      setSelectedReport(null);
      fetchReports();
    } catch (error) {
      message.error("Failed to ban user");
    }
  };

  const getCategoryColor = (category: ReportCategory) => {
    const colors = {
      [ReportCategory.SPAM]: "orange",
      [ReportCategory.HARASSMENT]: "red",
      [ReportCategory.INAPPROPRIATE_CONTENT]: "volcano",
      [ReportCategory.FAKE_PROFILE]: "purple",
      [ReportCategory.OFFENSIVE_LANGUAGE]: "magenta",
      [ReportCategory.SCAM]: "red",
      [ReportCategory.OTHER]: "default",
    };
    return colors[category] || "default";
  };

  const getUserName = (user: string | IUser) => {
    if (typeof user === "string") return user;
    return user?.name || user?.email || "Unknown";
  };

  const handleChangeStatus = async (
    reportId: string,
    newStatus: ReportStatus
  ) => {
    try {
      await Api.put(`/api/admin/panel/reports/${reportId}/status`, {
        status: newStatus,
      });
      message.success("Status updated successfully");
      fetchReports();
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  const columns = [
    {
      title: "Reporter",
      dataIndex: "reporterUserID",
      key: "reporterUserID",
      render: (user: string | IUser) => getUserName(user),
    },
    {
      title: "Target User",
      dataIndex: "targetUserID",
      key: "targetUserID",
      render: (user: string | IUser) => <strong>{getUserName(user)}</strong>,
    },
    {
      title: "Type",
      dataIndex: "reportType",
      key: "reportType",
      render: (type: ReportType) => (
        <Tag color={type === ReportType.USER ? "blue" : "cyan"}>
          {type.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category: ReportCategory) => (
        <Tag color={getCategoryColor(category)}>
          {category.replace(/_/g, " ").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: ReportStatus, record: IReport) => (
        <Select
          value={status}
          style={{ width: 140 }}
          size="small"
          onChange={(value) => handleChangeStatus(record._id, value)}
          options={[
            { value: ReportStatus.PENDING, label: "Pending" },
            { value: ReportStatus.UNDER_REVIEW, label: "Under Review" },
            { value: ReportStatus.RESOLVED, label: "Resolved" },
            { value: ReportStatus.REJECTED, label: "Rejected" },
          ]}
        />
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 100,
      align: "center" as const,
      render: (description: string) => {
        if (!description) return "-";
        return (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedDescription(description);
              setDescriptionModalVisible(true);
            }}
          />
        );
      },
    },
    {
      title: "Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (date: Date) => {
        const d = new Date(date);
        return `${d.toLocaleDateString()} ${d.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })}`;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_: any, record: IReport) => {
        const menuItems = [
          {
            key: "warn",
            label: "Send Warning",
            icon: <WarningOutlined />,
            onClick: () => {
              setSelectedReport(record);
              setWarningModalVisible(true);
            },
          },
          {
            key: "ban",
            label: "Ban User",
            icon: <StopOutlined />,
            danger: true,
            onClick: () => {
              setSelectedReport(record);
              setBanModalVisible(true);
            },
          },
        ];

        return (
          <Space>
            <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
              <Button type="link" icon={<MoreOutlined />} />
            </Dropdown>
            <Popconfirm
              title="Are you sure to delete this report?"
              onConfirm={() => handleDelete(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const pageSizeSelect = (
    <Select
      value={limit}
      style={{ width: isCompact ? "100%" : 100 }}
      onChange={(value) => {
        setLimit(value);
        setPage(1);
      }}
      options={[
        { value: 10, label: "10 / page" },
        { value: 25, label: "25 / page" },
        { value: 50, label: "50 / page" },
        { value: 100, label: "100 / page" },
      ]}
    />
  );

  const statusOptions = [
    { value: ReportStatus.PENDING, label: "Pending" },
    { value: ReportStatus.UNDER_REVIEW, label: "Under Review" },
    { value: ReportStatus.RESOLVED, label: "Resolved" },
    { value: ReportStatus.REJECTED, label: "Rejected" },
  ];

  const renderReportActions = (record: IReport) => {
    const menuItems = [
      {
        key: "warn",
        label: "Send Warning",
        icon: <WarningOutlined />,
        onClick: () => {
          setSelectedReport(record);
          setWarningModalVisible(true);
        },
      },
      {
        key: "ban",
        label: "Ban User",
        icon: <StopOutlined />,
        danger: true,
        onClick: () => {
          setSelectedReport(record);
          setBanModalVisible(true);
        },
      },
    ];

    return (
      <Space>
        <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
          <Button type="link" icon={<MoreOutlined />} />
        </Dropdown>
        <Popconfirm
          title="Are you sure to delete this report?"
          onConfirm={() => handleDelete(record._id)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    );
  };

  return (
    <div style={{ padding: isCompact ? 0 : 24 }}>
      <Card
        title="Reports"
        extra={
          isCompact ? undefined : (
            <Space>
              <span>Items per page:</span>
              {pageSizeSelect}
            </Space>
          )
        }
      >
        <div
          style={{
            display: "flex",
            flexDirection: isCompact ? "column" : "row",
            gap: 8,
            alignItems: isCompact ? "stretch" : "center",
            marginBottom: 16,
          }}
        >
          <Select
            mode="multiple"
            placeholder="Filter by status"
            value={statusFilter}
            style={{ width: isCompact ? "100%" : 300 }}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
            options={statusOptions}
          />
          {isCompact && pageSizeSelect}
        </div>
        {isCompact ? (
          <>
            <Spin spinning={loading}>
              {reports.length === 0 && !loading ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No reports" />
              ) : (
                reports.map((record) => {
                  const created = new Date(record.createdDate);
                  return (
                    <div
                      key={record._id}
                      style={{
                        background: "#fff",
                        border: "1px solid #f0f0f0",
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, wordBreak: "break-word" }}>
                            {getUserName(record.targetUserID)}
                          </div>
                          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                            Reported by {getUserName(record.reporterUserID)}
                          </div>
                        </div>
                        {renderReportActions(record)}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                          marginTop: 8,
                        }}
                      >
                        <Tag color={record.reportType === ReportType.USER ? "blue" : "cyan"}>
                          {record.reportType.toUpperCase()}
                        </Tag>
                        <Tag color={getCategoryColor(record.category)}>
                          {record.category.replace(/_/g, " ").toUpperCase()}
                        </Tag>
                      </div>
                      <Select
                        value={record.status}
                        size="small"
                        style={{ width: "100%", marginTop: 8 }}
                        onChange={(value) => handleChangeStatus(record._id, value)}
                        options={statusOptions}
                      />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: 8,
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 12, color: "#666" }}>
                          {created.toLocaleDateString()}{" "}
                          {created.toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        {record.description ? (
                          <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => {
                              setSelectedDescription(record.description ?? "");
                              setDescriptionModalVisible(true);
                            }}
                          >
                            Description
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </Spin>
            <Pagination
              style={{ marginTop: 8, textAlign: "center" }}
              size="small"
              simple
              current={page}
              pageSize={limit}
              total={total}
              onChange={(newPage) => setPage(newPage)}
            />
          </>
        ) : (
        <Table
          columns={columns}
          dataSource={reports}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            onChange: (newPage) => setPage(newPage),
            showTotal: (total) => `Total ${total} reports`,
            showSizeChanger: false,
          }}
        />
        )}
      </Card>

      <Modal
        title="Send Warning to User"
        open={warningModalVisible}
        onCancel={() => {
          setWarningModalVisible(false);
          setWarningMessage("");
          setSelectedReport(null);
        }}
        onOk={handleSendWarning}
        okText="Send Warning"
        width={isCompact ? "100%" : 520}
      >
        <div style={{ marginBottom: 16 }}>
          <p>
            <strong>Target User:</strong>{" "}
            {selectedReport && getUserName(selectedReport.targetUserID)}
          </p>
          <p>
            <strong>Report Category:</strong> {selectedReport?.category}
          </p>
        </div>
        <Input.TextArea
          rows={4}
          placeholder="Enter warning message to send to the user..."
          value={warningMessage}
          onChange={(e) => setWarningMessage(e.target.value)}
        />
      </Modal>

      <Modal
        title="Ban User"
        open={banModalVisible}
        onCancel={() => {
          setBanModalVisible(false);
          setBanMessage("");
          setBanPeriodDays(7);
          setSelectedReport(null);
        }}
        onOk={handleBanUser}
        okText="Ban User"
        okButtonProps={{ danger: true }}
        width={isCompact ? "100%" : 520}
      >
        <div style={{ marginBottom: 16 }}>
          <p>
            <strong>Target User:</strong>{" "}
            {selectedReport && getUserName(selectedReport.targetUserID)}
          </p>
          <p>
            <strong>Report Category:</strong> {selectedReport?.category}
          </p>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 8 }}>
            <strong>Ban Period:</strong>
          </label>
          <Select
            value={banPeriodDays}
            style={{ width: "100%" }}
            onChange={(value) => setBanPeriodDays(value)}
            options={[
              { value: 1, label: "1 Day" },
              { value: 3, label: "3 Days" },
              { value: 7, label: "7 Days" },
              { value: 14, label: "14 Days" },
              { value: 30, label: "30 Days" },
              { value: 90, label: "90 Days" },
              { value: 365, label: "1 Year" },
              { value: 3650, label: "Permanent (10 Years)" },
            ]}
          />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 8 }}>
            <strong>Ban Message:</strong>
          </label>
          <Input.TextArea
            rows={4}
            placeholder="Enter ban reason to send to the user..."
            value={banMessage}
            onChange={(e) => setBanMessage(e.target.value)}
          />
        </div>
      </Modal>

      <Modal
        title="Description"
        open={descriptionModalVisible}
        onCancel={() => {
          setDescriptionModalVisible(false);
          setSelectedDescription("");
        }}
        footer={[
          <Button key="close" onClick={() => setDescriptionModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {selectedDescription}
        </p>
      </Modal>
    </div>
  );
};

export default ReportListPage;
